import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import FuelCheckoutForm from "./FuelCheckoutForm";
import "./FuelUp.scss";
import { useCreateFuelDonationMutation } from "../features/api/donationApiSlice";
import FuelLoading from "./FuelLoading";
import FuelPublicFeed from "./FuelPublicFeed";
import PageHeader from "../components/PageHeader/PageHeader";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const fuelOptions = [
  {
    label: "1 Gallon",
    value: "gallon",
    price: "$4",
    description: "Adds around 40 miles to the journey.",
  },
  {
    label: "Half Tank",
    value: "half_tank",
    price: "$15",
    description: "Helps cover a quiet backroad detour.",
  },
  {
    label: "Full Tank",
    value: "full_tank",
    price: "$30",
    description: "Fuels a full Mini Miles road day.",
  },
  {
    label: "Custom",
    value: "custom",
    price: "Your amount",
    description: "Choose your own contribution.",
  },
];

export default function FuelUp() {
  const [selectedOption, setSelectedOption] = useState("gallon");
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [clientSecret, setClientSecret] = useState("");

  const [isPreparingCheckout, setIsPreparingCheckout] = useState(false);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const [formErrors, setFormErrors] = useState({
    customAmount: false,
    donorName: false,
    donorEmail: false,
  });
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [copied, setCopied] = useState(false); // Tracks clipboard status

  const [createFuelDonation, { isLoading }] = useCreateFuelDonationMutation();

  const handleInputChange = (setterFn, fieldName) => (e) => {
    setterFn(e.target.value);
    if (error) setError("");
    if (formErrors[fieldName]) {
      setFormErrors((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleOptionSelect = (value) => {
    setSelectedOption(value);
    setError("");
    setFormErrors({ customAmount: false, donorName: false, donorEmail: false });
    if (value !== "custom") {
      setCustomAmount("");
    }
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    if (error) setError("");
    if (formErrors.customAmount) {
      setFormErrors((prev) => ({ ...prev, customAmount: false }));
    }
    if (value === "") {
      setCustomAmount("");
      return;
    }
    if (Number(value) < 0) return;
    setCustomAmount(value);
  };

  const handleStartPayment = async () => {
    setError("");

    const newErrors = {
      customAmount: false,
      donorName: false,
      donorEmail: false,
    };

    let hasError = false;

    if (selectedOption === "custom") {
      const amount = Number(customAmount);

      if (!customAmount || Number.isNaN(amount) || amount < 4) {
        setError(
          amount < 4 && customAmount
            ? "Minimum custom donation is $4."
            : "Please enter a custom donation amount."
        );
        newErrors.customAmount = true;
        hasError = true;
      }
    }

    if (!donorName.trim()) {
      setError("Please enter your name.");
      newErrors.donorName = true;
      hasError = true;
    }

    if (!donorEmail.trim()) {
      setError("Please enter your email address for your receipt.");
      newErrors.donorEmail = true;
      hasError = true;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailRegex.test(donorEmail.trim())) {
        setError("Please enter a valid email address.");
        newErrors.donorEmail = true;
        hasError = true;
      }
    }

    if (hasError) {
      setFormErrors(newErrors);
      return;
    }

    setIsPreparingCheckout(true);

    try {
      const res = await createFuelDonation({
        optionType: selectedOption,
        customAmount,
        donorName: donorName.trim(),
        donorEmail: donorEmail.trim(),
        message: message.trim(),
        isPublic,
      }).unwrap();

      setClientSecret(res.clientSecret);
    } catch (err) {
      setError(err?.data?.message || "Unable to prepare payment.");
    } finally {
      setIsPreparingCheckout(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset text after 2 seconds
    } catch (err) {
      console.error("Failed to copy link", err);
    }
  };

  const getCounterClass = () => {
    if (message.length >= 240) return "fuel-counter limit-danger";
    if (message.length >= 200) return "fuel-counter limit-warning";
    return "fuel-counter";
  };

  // Inside FuelUp.jsx return block, swap the main view if loading:
  if (isLoading) {
    return (
      <main className="fuel-page">
        <FuelLoading text="Mapping your route to payment..." />
      </main>
    );
  }

  if (isSuccess) {
    return (
      <main className="fuel-page">
        <section className="fuel-success-card">
          <div className="success-icon">✨ 🚗 ✨</div>
          <h1>Tank Fueled!</h1>
          <p>
            Thank you, <strong>{donorName || "Road Tripper"}</strong>! Your
            contribution keeps the tires rolling and the adventures alive.
          </p>

          <div className="success-badge">⚡ Adventure Powered Up</div>

          <div className="success-actions">
            <button className="fuel-secondary-btn" onClick={handleCopyLink}>
              {copied ? "✅ Link Copied!" : "🔗 Copy Shareable Link"}
            </button>
            <button
              className="fuel-primary-btn"
              onClick={() => window.location.reload()}
            >
              Back to Road Feed
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="fuel-page">
      <PageHeader
        kicker="Support the road"
        title="Fuel the Car"
        subtitle=" Help Mini Miles Adventures keep following quiet roads, small towns,
          overlooks, and unexpected turns."
      />

      {!clientSecret ? (
        <section className="fuel-card fuel-fade-in">
          <h2>Choose a fuel amount</h2>

          <div className="fuel-options">
            {fuelOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`fuel-option ${
                  selectedOption === option.value ? "active" : ""
                }`}
                onClick={() => handleOptionSelect(option.value)}
              >
                <div className="fuel-option__top">
                  <strong>{option.label}</strong>
                  <span>{option.price}</span>
                </div>
                <p>{option.description}</p>
              </button>
            ))}
          </div>

          <div className="fuel-form-group">
            {selectedOption === "custom" && (
              <div className="fuel-input-wrapper">
                <label htmlFor="custom-amount">
                  Custom Amount <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  id="custom-amount"
                  className={`fuel-custom-input ${
                    formErrors.customAmount ? "input-error" : ""
                  }`}
                  type="number"
                  min="4"
                  step="1"
                  inputMode="numeric"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  placeholder="Minimum $4"
                />
              </div>
            )}

            <div className="fuel-input-wrapper">
              <label htmlFor="donor-name">
                Name <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                id="donor-name"
                type="text"
                className={formErrors.donorName ? "input-error" : ""}
                value={donorName}
                onChange={handleInputChange(setDonorName, "donorName")}
                placeholder="Anonymous Road Tripper"
              />
            </div>

            <div className="fuel-input-wrapper">
              <label htmlFor="donor-email">
                Email address <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                id="donor-email"
                type="email"
                className={formErrors.donorEmail ? "input-error" : ""}
                value={donorEmail}
                onChange={handleInputChange(setDonorEmail, "donorEmail")}
                placeholder="driver@roadtrip.com"
              />
            </div>

            <div className="fuel-input-wrapper">
              <label htmlFor="donor-message">Message (Optional)</label>
              <textarea
                id="donor-message"
                value={message}
                maxLength={250}
                onChange={handleInputChange(setMessage, "")}
                placeholder="Leave a road note..."
              />
              <div className={getCounterClass()}>
                {message.length} / 250 characters
              </div>
            </div>

            <label className="fuel-checkbox">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              <span>Show my name/message publicly</span>
            </label>
          </div>

          {error && <div className="fuel-error">{error}</div>}

          <button
            className="fuel-primary-btn"
            type="button"
            onClick={handleStartPayment}
            disabled={isLoading || isPreparingCheckout}
          >
            Continue to Payment
          </button>
        </section>
      ) : (
        <div className="fuel-fade-in">
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <FuelCheckoutForm onPaymentSuccess={() => setIsSuccess(true)} />
          </Elements>
        </div>
      )}

      {/* 2. RENDER AT BOTTOM: Display the live feed under the interactive elements */}
      {!isSuccess && <FuelPublicFeed />}
    </main>
  );
}
